import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

export const BeforeAfterSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const sliderId = `slider-${title?.replace(/\s+/g, '-').toLowerCase() || 'comparison'}`;

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    handleMove(e.touches[0].clientX);
  }, [isDragging, handleMove]);

  // Keyboard navigation for accessibility
  const handleKeyDown = useCallback((e) => {
    const step = 5;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setSliderPosition(prev => Math.max(0, prev - step));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setSliderPosition(prev => Math.min(100, prev + step));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSliderPosition(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSliderPosition(100);
    }
  }, []);

  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  return (
    <motion.div 
      className="w-full"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      {title && (
        <h3 id={`${sliderId}-title`} className="font-chivo font-bold text-2xl text-ktm-navy mb-4 text-center">
          {title}
        </h3>
      )}
      
      <div
        ref={containerRef}
        className="relative w-full aspect-[4/3] overflow-hidden cursor-ew-resize select-none border-4 border-ktm-navy"
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onMouseDown={() => setIsDragging(true)}
        onTouchStart={() => setIsDragging(true)}
        data-testid="before-after-slider"
        role="group"
        aria-labelledby={title ? `${sliderId}-title` : undefined}
        aria-label={!title ? "Before and after comparison" : undefined}
      >
        {/* After Image (Background) */}
        <img
          src={afterImage}
          alt={`${title || 'Project'} - After renovation`}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />

        {/* Before Image (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
          aria-hidden="true"
        >
          <img
            src={beforeImage}
            alt={`${title || 'Project'} - Before renovation`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ 
              width: containerRef.current ? containerRef.current.offsetWidth : '100%',
              maxWidth: 'none'
            }}
            draggable={false}
          />
        </div>

        {/* Slider Line - Interactive Element */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          role="slider"
          tabIndex={0}
          aria-label={`Comparison slider for ${title || 'before and after'}. Use arrow keys to adjust.`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(sliderPosition)}
          aria-valuetext={`Showing ${Math.round(sliderPosition)}% before image, ${Math.round(100 - sliderPosition)}% after image`}
          onKeyDown={handleKeyDown}
        >
          {/* Slider Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-ktm-red">
            <div className="flex items-center gap-1" aria-hidden="true">
              <svg className="w-3 h-3 text-ktm-navy" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              <svg className="w-3 h-3 text-ktm-navy" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 bg-ktm-navy text-white px-3 py-1 text-sm font-bold uppercase tracking-wider z-20" aria-hidden="true">
          Before
        </div>
        <div className="absolute top-4 right-4 bg-ktm-red text-white px-3 py-1 text-sm font-bold uppercase tracking-wider z-20" aria-hidden="true">
          After
        </div>
      </div>

      <p className="text-center text-slate-500 text-sm mt-3" id={`${sliderId}-instructions`}>
        <span className="sr-only">Instructions: </span>
        Drag the slider or use arrow keys to compare before and after
      </p>
    </motion.div>
  );
};

export default BeforeAfterSlider;
